import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "../../assets/css/app-install-banner.css";

const DISMISS_KEY = "aib_dismissed_v2";
const DISMISS_DAYS = 3;

function isApple() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isAndroid() {
  return /Android/i.test(navigator.userAgent);
}

function isMobile() {
  return isApple() || isAndroid() || window.matchMedia("(max-width: 768px)").matches;
}

function isStandalone() {
  return (
    window.navigator.standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches
  );
}

function wasDismissedRecently() {
  const ts = localStorage.getItem(DISMISS_KEY);
  if (!ts) return false;
  return Date.now() - parseInt(ts, 10) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

async function checkAppInstalled(platform) {
  try {
    if (platform === "android" && "getInstalledRelatedApps" in navigator) {
      const apps = await navigator.getInstalledRelatedApps();
      return apps.length > 0;
    }
  } catch (_) {}
  return false;
}

export default function AppInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [animOut, setAnimOut] = useState(false);
  const [storeUrl, setStoreUrl] = useState("");
  const [platform, setPlatform] = useState("");
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    if (!isMobile() || isStandalone() || wasDismissedRecently()) return;

    const apple = isApple();
    const android = isAndroid();
    const appleUrl = process.env.REACT_APP_APP_STORE_URL;
    const androidUrl = process.env.REACT_APP_GOOGLE_PLAY_URL;

    let detectedPlatform = null;
    let detectedUrl = null;

    if (apple && appleUrl) {
      detectedPlatform = "ios";
      detectedUrl = appleUrl;
    } else if (android && androidUrl) {
      detectedPlatform = "android";
      detectedUrl = androidUrl;
    }

    if (!detectedPlatform) {
      // Fallback for responsive/narrow screen when UA isn't mobile
      if (androidUrl) {
        detectedPlatform = "android";
        detectedUrl = androidUrl;
      } else if (appleUrl) {
        detectedPlatform = "ios";
        detectedUrl = appleUrl;
      } else {
        return;
      }
    }

    setPlatform(detectedPlatform);
    setStoreUrl(detectedUrl);

    checkAppInstalled(detectedPlatform)
      .then((installed) => {
        setIsInstalled(installed);
        setVisible(true);
      })
      .catch(() => {
        setVisible(true);
      });
  }, []);

  const dismiss = () => {
    setAnimOut(true);
    setTimeout(() => {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
      setVisible(false);
    }, 300);
  };

  if (!visible) return null;

  const ctaLabel = isInstalled
    ? "Open Zoomey App"
    : platform === "ios"
    ? "Download on App Store"
    : "Get it on Google Play";

  const PlatformIcon = platform === "ios" ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302-2.302 2.302-2.593-2.302 2.593-2.302zM5.864 2.658L16.8 8.991l-2.302 2.302-8.635-8.635z" />
    </svg>
  );

  return createPortal(
    <>
      <div className="aib-backdrop" onClick={dismiss} />

      <div className={`aib-sheet ${animOut ? "aib-sheet--out" : ""}`}>
        <div className="aib-handle" />

        <button className="aib-close" onClick={dismiss} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <div className="aib-app-row">
          <div className="aib-app-icon">
            <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
              <path
                d="M6 7H22L8 21H22"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="22" cy="7" r="2" fill="currentColor" />
            </svg>
          </div>
          <div className="aib-app-info">
            <span className="aib-app-name">Zoomey</span>
            <span className="aib-app-tagline">Run your work with a lighter mind</span>
            {isInstalled && (
              <span className="aib-app-installed-badge">
                <i className="bi bi-check-circle-fill"></i> Installed
              </span>
            )}
          </div>
        </div>

        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="aib-cta"
          onClick={dismiss}
        >
          {PlatformIcon}
          {ctaLabel}
        </a>

        <button className="aib-dismiss-link" onClick={dismiss}>
          Continue in browser
        </button>
      </div>
    </>,
    document.body
  );
}
